import { MAX_VALID_EPOCH, MIN_VALID_EPOCH } from "./constants_v2.js";

const isValidNumber = (number) => {
  if (typeof number != "number") {
    return false;
  }
  if (!Number.isInteger(number)) {
    return false;
  }
  return true;
};

const isEpochValid = (epochTime) => {
  if (!isValidNumber(epochTime)) {
    return false;
  }
  if (epochTime < MIN_VALID_EPOCH || epochTime > MAX_VALID_EPOCH) {
    return false;
  }
  return true;
};

const epochTime = () => {
  const now = new Date();
  const seconds = Math.floor(now.getTime() / 1000);
  return seconds;
};

const epochTimePlus2Days = () => {
  return epochTime() + 24 * 60 * 60 * 2; //48 hours. Let's not worry about daylight savings time
};

const epochTimeMinus31Days = () => {
  return epochTime() - 24 * 60 * 60 * 31; //31 days ago. Let's not worry about daylight savings time
};

const epochTimePlus1Year = () => {
  const now = new Date();
  now.setFullYear(now.getFullYear() + 1);
  const futureEpochSeconds = Math.floor(now.getTime() / 1000);
  return futureEpochSeconds;
};

const isEpochTimeMsValid = (epochTimeMs) => {
  if (!isValidNumber(epochTimeMs)) {
    return false;
  }
  if (
    epochTimeMs / 1000 < MIN_VALID_EPOCH ||
    epochTimeMs / 1000 > MAX_VALID_EPOCH
  ) {
    return false;
  }
  return true;
};

const epochTimeMs = () => {
  const now = new Date();
  const ms = Math.floor(now.getTime());
  return ms;
};

const isYearValid = (year) => {
  // Convert to number if it's a string (for backwards compatibility with DynamoDB stored strings)
  const yearNumber = typeof year === "string" ? parseInt(year, 10) : year;
  if (!isValidNumber(yearNumber)) {
    return false;
  }
  const minDateObject = new Date(MIN_VALID_EPOCH * 1000);
  const minYear = minDateObject.getFullYear();
  const maxDateObject = new Date(MAX_VALID_EPOCH * 1000);
  const maxYear = maxDateObject.getFullYear();

  if (yearNumber < minYear || yearNumber > maxYear) {
    return false;
  }
  return true;
};

const year = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  return currentYear;
};

const isMonthValid = (month) => {
  // Convert to number if it's a string
  const monthNumber = typeof month === "string" ? parseInt(month, 10) : month;
  if (!isValidNumber(monthNumber)) {
    return false;
  }
  if (monthNumber < 0 || monthNumber > 12) {
    return false;
  }
  return true;
};

const month = () => {
  const now = new Date();
  const monthNumber = now.getMonth() + 1;
  return String(monthNumber).padStart(2, "0");
};

const isDayValid = (day) => {
  // Convert to number if it's a string
  const dayNumber = typeof day === "string" ? parseInt(day, 10) : day;
  if (!isValidNumber(dayNumber)) {
    return false;
  }
  if (dayNumber < 0 || dayNumber > 31) {
    return false;
  }
  return true;
};

const day = () => {
  const now = new Date();
  const dayOfMonth = now.getDate();
  return dayOfMonth;
};

/**
 * @typedef {object} timeStamps - Contains timeStamps in easily retreivable formats
 * @property {number} epoch - Number of seconds since 1/1/1970
 * @property {number} year - the year in four digits: YYYY
 * @property {number} month - the month in two digits: MM
 * @property {number} day - the day in two digits: DD
 */

const areTimeStampsValid = (timeStamps) => {
  if (!(typeof timeStamps === "object")) {
    const message = "ERROR: The timeStamps are not an object.";
    return { status: 400, message };
  }
  if (timeStamps === null) {
    const message = "ERROR: The timeStamps are null.";
    return { status: 400, message };
  }
  if (Array.isArray(timeStamps)) {
    const message = "ERROR: The timeStamps are an array.";
    return { status: 400, message };
  }
  const keys = Object.keys(timeStamps);
  if (keys.length != 4) {
    const message =
      "ERROR: The timeStamps should have four keys, but got this many instead: " +
      keys.length;
    return { status: 400, message };
  }
  if (!keys.includes("epoch")) {
    const message = "ERROR: The timeStamps are missing epoch";
    return { status: 400, message };
  }
  if (!keys.includes("year")) {
    const message = "ERROR: The timeStamps are missing year";
    return { status: 400, message };
  }
  if (!keys.includes("month")) {
    const message = "ERROR: The timeStamps are missing month";
    return { status: 400, message };
  }
  if (!keys.includes("day")) {
    const message = "ERROR: The timeStamps are missing day";
    return { status: 400, message };
  }
  if (!isEpochValid(timeStamps.epoch)) {
    const message = "ERROR: The epoch is not valid: " + timeStamps.epoch;
    return { status: 400, message };
  }
  if (!isYearValid(timeStamps.year)) {
    const message = "ERROR: The year is not valid: " + timeStamps.year;
    return { status: 400, message };
  }
  if (!isMonthValid(timeStamps.month)) {
    const message = "ERROR: The month is not valid: " + timeStamps.month;
    return { status: 400, message };
  }
  if (!isDayValid(timeStamps.day)) {
    const message = "ERROR: The day is not valid: " + timeStamps.day;
    return { status: 400, message };
  }
  return { status: 200, message: "" };
};

const getTimeStamps = () => {
  return {
    epoch: epochTime(),
    year: year(),
    month: month(),
    day: day(),
  };
};

const compactDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  // Month is 0-indexed, so add 1 and pad with '0'
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const compactDateMMDDYYYY = () => {
  const date = new Date();
  const year = date.getFullYear();
  // Month is 0-indexed, so add 1 and pad with '0'
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");

  return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
};

export const dateTimeUtils = {
  getTimeStamps,
  isEpochValid,
  isEpochTimeMsValid,
  areTimeStampsValid,
  epochTime,
  epochTimePlus2Days,
  epochTimeMinus31Days,
  epochTimePlus1Year,
  epochTimeMs,
  year,
  month,
  day,
  compactDate,
  compactDateMMDDYYYY,
};
